import { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

import { getEventsByField, loadToken } from 'services/api/bks';
import moment from "moment";

import ScatterChart from "components/Charts/ScatterChart";

import { useAuth0 } from "@auth0/auth0-react";

import { event_types } from "components/Charts/DeviceEvents/event_types";

function getEventTypeParent(event_type) {
    var child = event_types.filter((e) => e.id == event_type);
    // console.log('event_type: ', event_type)

    if (child.length != 0 && typeof child[0].parent != 'undefined') {
        var parent = child[0].parent;
        // console.log('child parent: ', parent);
    }
    else {
        var parent = event_type;
        // console.log('parent: ', parent);
    }

    return parent;
}

function initTrimmedData(trimmed, device, advancedToggle) {

    // console.log(device.data);

    for (var event_type in device.data) {
        var event_type_trimmed = event_type.replace(/ /g, "_").trim();

        if (!advancedToggle) {
            var event_type_trimmed = getEventTypeParent(event_type_trimmed);
        }

        if (["POWER-ON_RESET_(POR)_/_POWER-D", "EXTERNAL_RESET_PIN_RESET", "WINDOW_WATCHDOG_RESET", "LOW_POWER_RESET", "BROWNOUT_RESET_(BOR)"].indexOf(event_type_trimmed) > -1) {
            event_type_trimmed = "OTHER_RESET";
        }

        if (typeof trimmed[event_type_trimmed] == 'undefined') {
            trimmed[event_type_trimmed] = {};
        }

        for (var row of device.data[event_type]) {
            if (typeof trimmed[event_type_trimmed][row.id] == 'undefined') {
                trimmed[event_type_trimmed][row.id] = {
                    count: row.count,
                    start_time: row.start
                };
            }
            else if (typeof trimmed[event_type_trimmed][row.id] != 'undefined') {
                trimmed[event_type_trimmed][row.id].count += row.count;
            }
        }
    }

    return trimmed;
}

function DeviceEventsChart({ uuid, advancedToggle, startTime, setStartTime, endTime, setEndTime, groupSize, height }) {
    // console.log(groupSize)
    loadToken(useAuth0());

    const chartRef = useRef(null);
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);

    // console.log(advancedToggle)

    var advanced_labels = advancedToggle ? event_types.filter(row => row.id != 'REBOOT') : event_types.filter(row => row.level == 1);

    async function processChartData(data) {
        var generated_data = {
            datasets: [],
            nick_labels: [],
            limits: [],
            labels: []
        };

        var trimmed_result = {
            start_time: startTime,
            end_time: endTime,
            group_size: groupSize,
            data: []
        };

        // console.log(advanced_labels);

        for (var device of data) {
            var trimmed_data = {
                data: [],
                uuid: device.uuid
            };

            // console.log(device);

            trimmed_result.data.push(trimmed_data);
            trimmed_data.data = initTrimmedData(trimmed_data.data, device, advancedToggle);

            for (var index in trimmed_data.data) {
                trimmed_data.data[index] = Object.values(trimmed_data.data[index]);
            }

            for (var event of event_types) {
                // console.log(event);
                if (advanced_labels.map(row => { return row.id }).indexOf(event.id) > -1) {

                    var times = typeof trimmed_data.data[event.id] == 'undefined' ? [] : trimmed_data.data[event.id];
                    var label = event.text;

                    if (times.length == 0) {
                        var dataset = {
                            label: label,
                            backgroundColor: event.colour.replace(')', ', 0.0)').replace('rgb', 'rgba'),
                            borderColor: event.colour.replace(')', ', 0.0)').replace('rgb', 'rgba'),
                            data: [{ x: moment(startTime).unix() * 1000, y: advanced_labels.map(row => { return row.id }).indexOf(event.id), data: null }],
                            pointRadius: (pt) => {
                                if (pt.raw.data) {
                                    return pt.raw.data.count >= 35 ? 35 : pt.raw.data.count + 2;
                                }
                            },
                            hoverRadius: (pt) => {
                                if (pt.raw.data) {
                                    return pt.raw.data.count >= 35 ? 39 : pt.raw.data.count + 6;
                                }
                            }
                        }
                    }
                    else {
                        var dataset = {
                            label: label,
                            backgroundColor: event.colour.replace(')', ', 0.7)').replace('rgb', 'rgba'),
                            borderColor: event.colour,
                            data: times.map((x_value) => {
                                return { x: x_value['start_time'] * 1000, y: advanced_labels.map(row => { return row.id }).indexOf(event.id), data: x_value }
                            }),
                            pointRadius: (pt) => {
                                if (pt.raw.data) {
                                    return pt.raw.data.count >= 35 ? 35 : pt.raw.data.count + 2;
                                }
                            },
                            hoverRadius: (pt) => {
                                if (pt.raw.data) {
                                    return pt.raw.data.count >= 35 ? 39 : pt.raw.data.count + 6;
                                }
                            }
                        }
                    }

                    // console.log(dataset);

                    generated_data.nick_labels.push(label);
                    generated_data.datasets.push(dataset);
                    generated_data.labels.push(label);
                }
            }
        }

        const chart_data = {
            data: generated_data,
        }

        return chart_data;
    }

    useEffect(() => {
        async function fetchData() {
            setLoading(true);

            var parent_event_types = event_types.filter(row => row.level == 1);
            var data = await getEventsByField(uuid, parent_event_types.map(row => { return row.id }), startTime.unix(), endTime.unix(), groupSize);
            var chart_data = await processChartData(data);

            setChartData(chart_data);
            setLoading(false);
        };
        fetchData();
    }, [startTime, endTime, advancedToggle]);

    return (
        <SoftBox display="flex" justifyContent="center" alignItems="center" width="100%">
            <Card sx={{ width: "100%" }}>
                <SoftBox p={2}>
                    <SoftBox sx={{ opacity: loading ? 0.3 : 1 }}>
                        <SoftBox m={1} display="flex" justifyContent="center" alignItems="center">
                            <SoftTypography variant="h6">
                                Device Events
                            </SoftTypography>
                        </SoftBox>
                        <SoftBox sx={{ height }}>
                            {chartData && <ScatterChart chartData={chartData} advancedToggle={advancedToggle} advanced_labels={advanced_labels} startTime={startTime.unix() * 1000} endTime={endTime.unix() * 1000} groupSize={groupSize} setStartTime={setStartTime} setEndTime={setEndTime} />}
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            </Card>
        </SoftBox>
    );

}

// Typechecking props for the DeviceEventsChart
DeviceEventsChart.propTypes = {
    uuid: PropTypes.string,
    advancedToggle: PropTypes.bool,
    startTime: PropTypes.instanceOf(moment()),
    setStartTime: PropTypes.func,
    endTime: PropTypes.instanceOf(moment()),
    setEndTime: PropTypes.func,
    groupSize: PropTypes.number,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DeviceEventsChart;

